"use client";

import * as React from "react";
import {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@workspace/ui/components/carousel";
import { useEmblaHorizontalScrollAnalytics } from "@/components/analytics/use-embla-horizontal-scroll-analytics";
import { useInView } from "@/hooks/use-in-view";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@workspace/ui/lib/utils";
import { SectionTypes } from "@/lib/utils/analytics";
import { StoryCard } from "./story-card";
import { StoryControls } from "./story-controls";
import { StoryFilmDialog, type TechStoryFilm } from "./story-film-dialog";
import { StoryFilmButton } from "./story-film-button";
import { useControlsReveal } from "./use-controls-reveal";
import { useStoryAutoplay } from "./use-story-autoplay";

// MP4 (H.264) only: every browser plays it, and iOS Safari fails to loop WebM
// (the video freezes on its last frame while still reporting that it's playing).
export type TechStoryVideo = {
  mp4: string;
  /** Served via <source media="(max-width: 767px)">. */
  mobileMp4?: string;
  poster: string;
};

export type TechStory = {
  id: string;
  title: React.ReactNode;
  body: React.ReactNode;
  video?: TechStoryVideo;
  /** Still fallback when there is no video. */
  image?: string;
  /** CSS object-position for `image`. */
  imagePosition?: string;
  /** Progress-fill duration; should match one loop of the video. */
  durationMs: number;
  /** Same, for the mobile clip when its length differs. */
  mobileDurationMs?: number;
};

export type TechStoriesSectionProps = {
  title: React.ReactNode;
  ctaLabel: string;
  stories: TechStory[];
  footnote?: React.ReactNode;
  /** Opened by the header CTA; without it the CTA isn't rendered. */
  film?: TechStoryFilm;
};

const FALLBACK_DURATION_MS = 6000;

export function TechStories({
  title,
  ctaLabel,
  stories,
  footnote,
  film,
}: TechStoriesSectionProps) {
  const [filmOpen, setFilmOpen] = React.useState(false);
  // Resolves before the video sources attach (they wait for the section to near the viewport).
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  // Same breakpoint as the cards' mobile <source>, so the progress matches the clip that plays.
  const isMobileClip = useMediaQuery("(max-width: 767px)");
  const rootRef = React.useRef<HTMLElement>(null);
  const [api, setApi] = React.useState<CarouselApi>();
  const count = stories.length;

  const {
    index,
    runId,
    displayStatus,
    manual,
    running,
    toggle,
    goTo,
    onProgressEnd,
    setBuffering,
    focusHandlers,
  } = useStoryAutoplay({
    api,
    count,
    rootRef,
    // The slider holds while the film plays over it.
    suspended: filmOpen,
  });

  // Attach video sources/posters shortly before the section scrolls into view.
  const shouldLoad = useInView(rootRef, {
    rootMargin: "400px 0px",
    once: true,
  });

  // The controls bar rises once the section's top is 40% of the way up the screen, then sticks to the
  // bottom of the viewport until the section ends (like Apple's "Get the highlights" gallery).
  const controlsRevealed = useControlsReveal(rootRef);

  useEmblaHorizontalScrollAnalytics({
    api,
    sectionType: SectionTypes.PRODUCT_TECH_STORIES,
  });

  const activeStory = stories[index];
  const durationMs =
    (isMobileClip && activeStory?.mobileDurationMs) ||
    activeStory?.durationMs ||
    FALLBACK_DURATION_MS;

  return (
    <section
      ref={rootRef}
      id="tech-stories"
      data-section-type="product_tech_stories"
      aria-labelledby="tech-stories-title"
      {...focusHandlers}
      className="relative w-full overflow-clip bg-black px-8 lg:bg-white lg:px-10 lg:pb-[120px] xl:px-20"
    >
      {/* Same frame as the other sections: section padding + a 1440px content column. */}
      {/* overflow-clip, not hidden: a hidden overflow would become the sticky controls' scroller. */}
      <div className="mx-auto w-full max-w-[1440px]">
        {/* The section's top padding, as a block inside the column: sticky elements can't rise above
            their parent's content box, so this lets the controls stick from the section's first pixels. */}
        <div aria-hidden="true" className="h-20 lg:h-[120px]" />
        <div className="flex flex-col items-center gap-5 lg:flex-row lg:items-end lg:justify-between lg:gap-10">
          <h2
            id="tech-stories-title"
            data-testid="tech-stories-title"
            className="title-32 text-center text-white lg:title-40 lg:max-w-[601px] lg:text-left lg:text-primary"
          >
            {title}
          </h2>
          {/* The CTA only exists to open the film. Mobile shows it here; desktop shows it on each card. */}
          {film ? (
            <StoryFilmButton
              id="tech-stories-cta"
              label={ctaLabel}
              onClick={() => setFilmOpen(true)}
              className="flex lg:hidden"
            />
          ) : null}
        </div>

        <Carousel
          id="tech-stories-hscroll"
          data-testid="tech-stories-hscroll"
          setApi={setApi}
          // No loop: the slider stops at the first and last card; autoplay rewinds from the last to the first.
          // containScroll off keeps one snap per slide (the autoplay index is the snap index).
          // Desktop: keepSnaps (still one snap per slide) ends the last card flush with the column's
          // right edge, with the previous card peeking in on the left.
          opts={{
            align: "start",
            loop: false,
            duration: 30,
            containScroll: false,
            breakpoints: {
              "(min-width: 1024px)": { containScroll: "keepSnaps" },
            },
          }}
          // Mobile: the track breaks out to the screen's right edge; desktop stays in the column.
          className="-mr-8 mt-6 lg:mr-0 lg:mt-12"
        >
          <CarouselContent
            id="tech-stories-slides"
            data-testid="tech-stories-slides"
            className="ml-0"
            aria-live={running ? "off" : "polite"}
          >
            {stories.map((story, i) => (
              <CarouselItem
                key={story.id}
                id={`tech-stories-slide-${i + 1}`}
                data-testid={`tech-stories-slide-${i + 1}`}
                aria-label={`${i + 1} of ${count}`}
                // Gutter is slide padding, not flex gap. Desktop: the last slide has none, so it ends
                // flush with the column edge. h-auto undoes the item's h-full so the track stretches
                // every slide (and its card) to the tallest card.
                className="h-auto basis-[calc(min(366px,calc(100vw-64px))+16px)] pr-4 pl-0 lg:basis-[calc(min(1183px,calc(100%-96px))+32px)] lg:pr-8 lg:last:basis-[min(1183px,calc(100%-96px))] lg:last:pr-0"
              >
                <StoryCard
                  story={story}
                  index={i}
                  active={i === index}
                  autoPlay={!reducedMotion}
                  shouldLoad={shouldLoad}
                  onBufferingChange={setBuffering}
                  ctaLabel={ctaLabel}
                  onWatchFilm={film ? () => setFilmOpen(true) : undefined}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        {/* Sticky within the content column: it floats over the cards at the bottom of the viewport
            and settles into place under them at the end. Hidden below the screen until revealed. */}
        <div
          id="tech-stories-controls-bar"
          data-testid="tech-stories-controls-bar"
          inert={!controlsRevealed}
          className={cn(
            "sticky bottom-5 z-10 mt-[30px] mb-5 [transition:transform_700ms_cubic-bezier(0.16,1,0.3,1),opacity_300ms_ease-out] motion-reduce:transition-none lg:bottom-8 lg:mt-12 lg:mb-0",
            controlsRevealed
              ? "translate-y-0 opacity-100"
              : "pointer-events-none translate-y-[180px] opacity-0",
          )}
        >
          <StoryControls
            count={count}
            index={index}
            runId={runId}
            status={displayStatus}
            manual={manual}
            running={running}
            durationMs={durationMs}
            onSelect={goTo}
            onToggle={toggle}
            onProgressEnd={onProgressEnd}
          />
        </div>

        {footnote ? (
          <p
            id="tech-stories-footnote"
            data-testid="tech-stories-footnote"
            className="pt-5 pb-8 text-[8px] leading-[1.33] tracking-[-0.2px] text-light-grey/50 lg:hidden"
          >
            {footnote}
          </p>
        ) : null}
        {film ? (
          <StoryFilmDialog
            film={film}
            open={filmOpen}
            onOpenChange={setFilmOpen}
            videoType="spectral_mapping_film"
          />
        ) : null}
      </div>
    </section>
  );
}
